import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ShieldCheck, Zap, Scale, Users2, ArrowRight, CheckCircle2,
  Lock, TrendingUp, Building2, LayoutDashboard, Mail, Phone,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import Chat from "@/components/Chat";
import { LOGO } from "@/lib/brand";
import { getPublicSettings } from "@/lib/api";

const HERO = "https://images.unsplash.com/photo-1583855604333-8981393dfde5?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const DEFAULT_LANDING = {
  badge: "La legalità come infrastruttura", titolo: "Scopri in", titolo_evidenziato: "60 secondi",
  titolo_coda: "se puoi accedere alla cessione del quinto",
  sottotitolo: "Valutazione algoritmica trasparente, consenso esplicito e tutela garantita. Nessuna intermediazione opaca: solo un percorso chiaro verso un consulente iscritto OAM.",
  cta: "Inizia la verifica gratuita", cta_nota: "Gratis · Senza impegno · GDPR compliant",
  chat_titolo: "Parla con il nostro assistente",
  chat_sottotitolo: "Niente moduli freddi: un assistente intelligente ti guida con poche domande e calcola in tempo reale la tua fattibilità, inclusi rinnovi ed estensioni dei finanziamenti in corso.",
  visione_titolo: "Non vendiamo lead. Costruiamo il primo sistema creditizio regolamentato d'Europa.",
  footer_testo: "Il servizio si limita alla trasmissione dei dati a partner finanziari autorizzati e non costituisce erogazione diretta di credito. Dati trattati ai sensi del GDPR UE 2016/679.",
  contatto_email: "", contatto_telefono: "",
};

export default function Home() {
  const quizRef = useRef(null);
  const [L, setL] = useState(DEFAULT_LANDING);
  const [assistantName, setAssistantName] = useState("Assistente Leadders");

  useEffect(() => {
    getPublicSettings().then((s) => {
      setL({ ...DEFAULT_LANDING, ...s.landing });
      if (s.chatbot?.nome_assistente) setAssistantName(s.chatbot.nome_assistente);
    }).catch(() => {});
  }, []);

  const scrollToQuiz = () => quizRef.current?.scrollIntoView({ behavior: "smooth" });

  return (
    <div className="min-h-screen bg-background">
      {/* Nav */}
      <header className="sticky top-0 z-50 backdrop-blur-md bg-background/80 border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src={LOGO} alt="Leadders" className="h-9 w-auto" data-testid="nav-logo" />
          </div>
          <nav className="flex items-center gap-2">
            <Button data-testid="nav-dashboard-link" asChild variant="ghost" size="sm" className="gap-2">
              <Link to="/dashboard"><LayoutDashboard className="h-4 w-4" /> Dashboard</Link>
            </Button>
            <Button data-testid="nav-quiz-button" size="sm" onClick={scrollToQuiz}
              className="bg-accent hover:bg-accent/90 text-white">Verifica gratuita</Button>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden grain">
        <div className="absolute inset-0 bg-primary" />
        <img src={HERO} alt="" className="absolute inset-0 w-full h-full object-cover opacity-20 mix-blend-luminosity" />
        <div className="absolute inset-0 bg-gradient-to-t from-primary via-primary/90 to-primary/70" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="max-w-3xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-accent/30 bg-accent/10 px-4 py-1.5 mb-6">
              <ShieldCheck className="h-4 w-4 text-accent" />
              <span className="text-sm font-medium text-accent" data-testid="hero-badge">{L.badge}</span>
            </div>
            <h1 data-testid="hero-title" className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-[1.05]">
              {L.titolo} <span className="text-gradient-brand">{L.titolo_evidenziato}</span> {L.titolo_coda}
            </h1>
            <p data-testid="hero-subtitle" className="text-lg sm:text-xl text-slate-300 mt-6 max-w-2xl leading-relaxed">
              {L.sottotitolo}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 mt-8">
              <Button data-testid="hero-cta-button" size="lg" onClick={scrollToQuiz}
                className="bg-accent hover:bg-accent/90 text-white text-base px-8 h-14 gap-2 font-semibold">
                {L.cta} <ArrowRight className="h-5 w-5" />
              </Button>
              <div className="flex items-center gap-2 text-slate-300 text-sm px-4">
                <CheckCircle2 className="h-5 w-5 text-emerald-400" /> {L.cta_nota}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Trust bar */}
      <section className="border-b border-border bg-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 grid grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { icon: Zap, t: "Risposta < 30s", d: "Speed-to-lead imbattibile" },
            { icon: Lock, t: "Consenso GDPR", d: "Separato e tracciato" },
            { icon: Scale, t: "Conforme OAM", d: "Consulenti regolamentati" },
            { icon: TrendingUp, t: "Scoring trasparente", d: "Ogni decisione spiegabile" },
          ].map((f) => (
            <div key={f.t} className="flex items-start gap-3">
              <f.icon className="h-6 w-6 text-accent shrink-0 mt-0.5" />
              <div>
                <div className="font-display font-semibold text-primary">{f.t}</div>
                <div className="text-sm text-muted-foreground">{f.d}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Quiz section */}
      <section ref={quizRef} className="py-16 sm:py-24 bg-background scroll-mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 max-w-2xl mx-auto">
            <h2 data-testid="chat-section-title" className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-primary">
              {L.chat_titolo}
            </h2>
            <p className="text-muted-foreground mt-3">{L.chat_sottotitolo}</p>
          </div>
          <Chat assistantName={assistantName} />
        </div>
      </section>

      {/* Vision — 3 levels */}
      <section className="py-16 sm:py-24 bg-primary grain relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="max-w-2xl mb-12">
            <span className="text-xs font-bold uppercase tracking-wider text-accent">La visione</span>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white mt-2">
              {L.visione_titolo}
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: Users2, t: "Il cittadino", d: "Da bersaglio a soggetto tutelato: consenso tracciato, percorso chiaro, nessuno viene abbandonato." },
              { icon: Building2, t: "Il consulente", d: "Da zona grigia a operatore regolamentato: CRM, scoring, pipeline e follow-up automatici." },
              { icon: Scale, t: "Il sistema Paese", d: "Banche con pratiche conformi, mercato tracciabile, AI usata per proteggere non discriminare." },
            ].map((c, i) => (
              <motion.div key={c.t} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                className="rounded-2xl border border-white/10 bg-white/5 p-7 backdrop-blur">
                <c.icon className="h-8 w-8 text-accent mb-4" />
                <h3 className="font-display text-xl font-semibold text-white">{c.t}</h3>
                <p className="text-slate-300 mt-2 leading-relaxed">{c.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-950 border-t border-white/10 py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex flex-col items-center sm:items-start gap-2">
            <div className="flex items-center gap-2">
              <Scale className="h-5 w-5 text-accent" />
              <span className="font-display font-bold text-white">Leadders</span>
            </div>
            {(L.contatto_email || L.contatto_telefono) && (
              <div className="flex flex-wrap gap-4 text-xs text-slate-400" data-testid="footer-contacts">
                {L.contatto_email && <a href={`mailto:${L.contatto_email}`} className="inline-flex items-center gap-1 hover:text-white"><Mail className="h-3.5 w-3.5" />{L.contatto_email}</a>}
                {L.contatto_telefono && <a href={`tel:${L.contatto_telefono}`} className="inline-flex items-center gap-1 hover:text-white"><Phone className="h-3.5 w-3.5" />{L.contatto_telefono}</a>}
              </div>
            )}
          </div>
          <p className="text-sm text-slate-400 text-center sm:text-right max-w-xl">{L.footer_testo} <Link to="/cookie-policy" className="underline" data-testid="footer-cookie-link">Cookie Policy</Link></p>
        </div>
      </footer>
    </div>
  );
}
